import type { ButtonProps as HeroButtonProps } from "@heroui/react";
import type { CSSProperties, ReactNode } from "react";
import type { ButtonProps } from "./Button";
import ButtonContent from "./ButtonContent";
import { VARIANT_MAP } from "./buttonConstants";
import {
  getButtonClassName,
  getButtonStyle,
  getDefaultButtonColor,
} from "./buttonUtils";

/**
 * HeroUI v3 button props, but with `style` and `children` narrowed to the
 * concrete values this helper produces — so the result also works on a plain
 * `<a>`/`<Link>` (LinkButton).
 */
export type ResolvedButtonProps = Omit<
  HeroButtonProps,
  "style" | "children"
> & {
  style: CSSProperties | undefined;
  children: ReactNode;
};

/**
 * Resolves DS Button props into the variant, style, className and children
 * consumed by the underlying HeroUI v3 button (or button-styled link). Shared
 * by Button, LinkButton, and other DS-styled triggers such as toast actions.
 */
export function resolveButtonProps({
  variant = "primary",
  color = getDefaultButtonColor(variant),
  size = "md",
  icon,
  label,
  children,
  className,
  isActive,
  pillShape,
  dropdownChevron,
  isLoading,
  isIconOnly,
  ...rest
}: ButtonProps): ResolvedButtonProps {
  return {
    ...rest,
    variant: VARIANT_MAP[variant],
    size,
    isIconOnly,
    style: getButtonStyle(variant, color),
    className: getButtonClassName(
      variant,
      size,
      pillShape,
      isActive,
      className,
    ),
    isPending: isLoading,
    children: (
      <ButtonContent
        icon={icon}
        label={label}
        dropdownChevron={dropdownChevron}
        size={size}
        isLoading={isLoading}
        isIconOnly={isIconOnly}
      >
        {children}
      </ButtonContent>
    ),
  };
}
